import { BrushTool } from "../../shared/boards";
import { byId } from "../dom";
import { t } from "../i18n";
import { BrushGroup, BrushSettings, GROUPS, TOOL_FEEL, brushDefaults, brushesIn, paintStroke, sampleStroke } from "./whiteboard_ink";
import { Kit } from "./whiteboard_kit";

// the brush shelf: every brush drawn live in its own style, grouped, with the settings of the one you pick

export interface BrushHost {
  kit: () => Kit;
  // the brush or retouch tool in hand, which the shelf shows settings for
  current: () => BrushTool;
  paper: () => string;
  pick: (tool: BrushTool) => void;
  // a setting moved, so the kit should be saved
  changed: () => void;
}

const SLIDERS: Array<{ key: keyof BrushSettings; label: string; show: (tool: BrushTool) => boolean }> = [
  { key: "smooth", label: "steady", show: () => true },
  { key: "press", label: "pressure", show: (tool) => TOOL_FEEL[tool].blend !== "multiply" },
  { key: "soft", label: "soft", show: (tool) => !TOOL_FEEL[tool].scatter && TOOL_FEEL[tool].anim !== "march" && TOOL_FEEL[tool].anim !== "neon" },
  { key: "gap", label: "spread", show: (tool) => Boolean(TOOL_FEEL[tool].scatter) }
];

let host: BrushHost;
let group: BrushGroup = "draw";
const tiles = new Map<BrushTool, HTMLCanvasElement>();
const samples = new Map<string, ReturnType<typeof sampleStroke>>();
let frame = 0;
let lastPaint = 0;

// the eraser has its own key, so it isn't on the shelf
function shelf(): BrushTool[] {
  return brushesIn(group).filter((tool) => tool !== "eraser");
}

function open(): boolean {
  return !byId("wb-brush-panel").hidden;
}

function sample(tool: BrushTool, width: number, height: number) {
  const kit = host.kit();
  const settings = kit.settings[tool];
  const key = `${tool}|${kit.color}|${width}|${JSON.stringify(settings)}`;
  let stroke = samples.get(key);
  if (!stroke) {
    if (samples.size > 80) samples.clear();
    const size = Math.max(3, Math.min(kit.sizes[tool], 34)) * (height / 56);
    // the eraser has nothing to show on a blank tile, so it previews as a plain grey line
    stroke = tool === "eraser"
      ? { ...sampleStroke("pen", width, height, size * 0.7, "#8a8f9c", brushDefaults("pen")), id: "sampleeraser" }
      : sampleStroke(tool, width, height, size, kit.color, settings);
    samples.set(key, stroke);
  }
  return stroke;
}

function paintTile(tool: BrushTool, canvas: HTMLCanvasElement, time: number): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (TOOL_FEEL[tool].group === "retouch") {
    // a retouch tool has nothing to show on blank paper, so it is dragged across three bands of colour
    const bands = [host.kit().color, "#ffc94d", "#4de8ff"];
    bands.forEach((color, at) => {
      ctx.fillStyle = color;
      ctx.fillRect((canvas.width * at) / 3, 0, canvas.width / 3 + 1, canvas.height);
    });
  }
  paintStroke(ctx, sample(tool, canvas.width, canvas.height), time, tool === "eraser" ? 1 : TOOL_FEEL[tool].opacity);
}

function tick(now: number): void {
  frame = 0;
  if (!open()) return;
  frame = requestAnimationFrame(tick);
  if (now - lastPaint < 50) return;
  lastPaint = now;
  for (const tool of shelf()) {
    if (TOOL_FEEL[tool].anim) {
      const canvas = tiles.get(tool);
      if (canvas) paintTile(tool, canvas, now);
    }
  }
}

function paintGrid(): void {
  const kit = host.kit();
  const grid = byId("wb-brush-grid");
  grid.setAttribute("role", "radiogroup");
  grid.setAttribute("aria-label", "brushes");
  tiles.clear();
  const paper = host.paper();
  grid.replaceChildren(...shelf().map((tool) => {
    const key = document.createElement("button");
    key.type = "button";
    key.className = "wb-brush";
    key.setAttribute("role", "radio");
    key.setAttribute("aria-checked", String(tool === host.current()));
    key.setAttribute("aria-label", TOOL_FEEL[tool].label);
    key.classList.toggle("active", tool === host.current());
    key.style.background = paper;
    const canvas = document.createElement("canvas");
    canvas.width = 220;
    canvas.height = 96;
    const label = document.createElement("span");
    label.textContent = t(TOOL_FEEL[tool].label);
    key.append(canvas, label);
    if (TOOL_FEEL[tool].anim) {
      const live = document.createElement("u");
      live.textContent = t("live");
      key.append(live);
    }
    key.addEventListener("click", () => {
      host.pick(tool);
      paint();
    });
    tiles.set(tool, canvas);
    paintTile(tool, canvas, 0);
    return key;
  }));
}

function paintSettings(): void {
  const kit = host.kit();
  const tool = host.current();
  const rows = SLIDERS.filter((slider) => slider.show(tool)).map((slider) => {
    const row = document.createElement("label");
    row.className = "wb-set";
    const name = document.createElement("span");
    name.textContent = t(slider.label);
    const input = document.createElement("input");
    input.type = "range";
    input.min = "0";
    input.max = "100";
    input.value = String(Math.round(kit.settings[tool][slider.key] * 100));
    const value = document.createElement("b");
    value.textContent = input.value;
    input.addEventListener("input", () => {
      kit.settings[tool][slider.key] = Number(input.value) / 100;
      value.textContent = input.value;
      host.changed();
      repaintCurrent();
    });
    row.append(name, input, value);
    return row;
  });
  const reset = document.createElement("button");
  reset.type = "button";
  reset.className = "pill small";
  reset.textContent = t("reset this brush");
  reset.addEventListener("click", () => {
    kit.settings[tool] = brushDefaults(tool);
    kit.sizes[tool] = TOOL_FEEL[tool].size;
    kit.alphas[tool] = TOOL_FEEL[tool].opacity;
    host.changed();
    paint();
  });
  byId("wb-brush-settings").replaceChildren(...rows, reset);
  byId("wb-brush-name").textContent = t(TOOL_FEEL[tool].label);
}

function repaintCurrent(): void {
  const tool = host.current();
  const canvas = tiles.get(tool);
  if (canvas) paintTile(tool, canvas, performance.now());
}

export function paint(): void {
  if (!host) return;
  byId("wb-brush-tabs").querySelectorAll<HTMLElement>("[data-group]").forEach((tab) => tab.classList.toggle("active", tab.dataset.group === group));
  paintGrid();
  paintSettings();
  if (open() && !frame) frame = requestAnimationFrame(tick);
}

// called when the panel is shown, so previews pick up the colour and the live ones start moving
export function showBrushes(): void {
  group = TOOL_FEEL[host.current()].group;
  buildTabs();
  paint();
  if (!frame) frame = requestAnimationFrame(tick);
}

// built when the shelf opens, so the labels follow the language
function buildTabs(): void {
  byId("wb-brush-tabs").replaceChildren(...GROUPS.map((item) => {
    const tab = document.createElement("button");
    tab.type = "button";
    tab.className = "tab";
    tab.dataset.group = item.id;
    tab.textContent = t(item.label);
    tab.addEventListener("click", () => {
      group = item.id;
      paint();
    });
    return tab;
  }));
}

export function setupBrushPanel(given: BrushHost): void {
  host = given;
}
